// @givmo/org-console — the landing rule, ONE pure function for every host (shell contract v3, T1).
//
// ══ THE ROOT IS A DOORWAY, AND THIS IS WHAT IT DECIDES ═════════════════════════════════════════
//
// A console's root never renders: it replaces itself with wherever this person belongs. Where that
// is used to be answered by each host — one of them with a Global-Admin branch that forwarded every
// administrator to the organization's management page. Contract v3 deletes that branch (R1: the
// landing is the class default module, for everyone, a Global Admin included) and promotes the
// rule into this package so the two class consoles cannot answer differently.
//
// ══ THE PRECEDENCE, OVER THE OPENABLE SET, EXCLUDING THE ADMIN CONSOLE TILE ═══════════════════
//
//   (a) the admin's CHOSEN default module, if openable → it
//   (b) on a COLD entry only: the LAST module hint, if openable → it
//   (c) EXACTLY ONE openable module → it
//   (d) the CLASS DEFAULT module, if openable → it
//   (e) the apps page — the management page for a holder of an Admin Console capability, the
//       "nothing openable" statement page for everyone else; the host decides which at that path.
//
// ══ TWO ENTRY KINDS, AND WHY (b) IS SKIPPED FOR ONE OF THEM ════════════════════════════════════
//
// `cold` is a generic-URL arrival — a bookmark, a typed address, the post-sign-in return — and the
// last-module hint exists precisely for it ("next time they go to a generic URL"). `in-app` is a
// control inside the app that MEANS "my landing" — the wordmark — and from inside module X with no
// chosen default it must land on the class default Y, never reload X because X is what the hint
// remembers. The two sentences of the ruling reconcile only this way.
//
// ⚠ NOTHING HERE NAMES A CLASS. The class default arrives as a row flag; the hint and the chosen
// default arrive as slugs; the openable set arrives already derived. Both consoles, one rule.
//
// ⚠ A DARK OR UNREPORTED STANDING STILL LANDS THE CLASS DEFAULT. With standings `null` every built
// tile is `link`, so a newly claimed administrator lands in the class default while the org read is
// still dark, and the module's own reads answer for themselves behind the gate. The old rule forwarded
// an unreported standing to the grid; that branch protected a Global-Admin landing that no longer
// exists, and it is gone with it.

import { openableSquares, type ConsoleSquare, type ConsoleTableRow } from './console-squares';

/** How one console entry was reached. See the header. */
export type LandingEntry = 'cold' | 'in-app';

export interface LandingInputs<T extends ConsoleTableRow = ConsoleTableRow> {
  entry: LandingEntry;
  /** The admin's chosen default module (`default_landing_module` off the standing read), or `null`. */
  preferredModule: string | null;
  /** The last-module hint, read by the doorway on a cold entry. Ignored on an in-app entry. */
  lastModule: string | null;
  /** The derived squares (`deriveConsoleSquares`) — the openable set is read off them here. */
  squares: readonly ConsoleSquare<T>[];
  /** The class default's slug (`classDefaultSlug`), or `null` for a table that names none. */
  classDefault: string | null;
  /**
   * The apps page — branch (e). A host names its own path; the default is the estate's `/apps`.
   * Never `/`: the root is the doorway, and a landing that pointed back at it would loop.
   */
  appsPath?: string;
}

/**
 * Where the person belongs.
 *
 * `in-app` is a router navigation; `external` keeps the whole-document `location.replace` hop a
 * cross-origin module needs. The `branch` names which rule fired, for a host's own log or test —
 * it is never rendered.
 */
export interface LandingDestination {
  kind: 'in-app' | 'external';
  href: string;
  branch: 'chosen-default' | 'last-module' | 'only-openable' | 'class-default' | 'apps';
}

export const DEFAULT_APPS_PATH = '/apps';

function destinationOf<T extends ConsoleTableRow>(
  square: ConsoleSquare<T>,
  branch: LandingDestination['branch'],
): LandingDestination {
  // `openableSquares` guarantees a destination; the guard is for a JS caller that outran the types.
  const destination = square.destination ?? { kind: 'in-app' as const, href: DEFAULT_APPS_PATH };
  return { kind: destination.kind, href: destination.href, branch };
}

/**
 * The landing rule, as a pure function so it can be pinned without a router.
 *
 * ⚠ THE ORDER OF THE BRANCHES IS THE CONTRACT — see the header. Two cases that look like they could
 * be folded and must not be: (c) and (d) cannot disagree when both apply, but (c) stays because a
 * class default may be UNOPENABLE for this person (not held; switched off by the organization) while
 * exactly one other module is; and (b) is consulted on a cold entry ONLY.
 */
export function landingDestination<T extends ConsoleTableRow>(
  inputs: LandingInputs<T>,
): LandingDestination {
  const appsPath = inputs.appsPath ?? DEFAULT_APPS_PATH;
  const openable = openableSquares(inputs.squares);
  const find = (slug: string | null): ConsoleSquare<T> | undefined =>
    slug === null || slug.trim().length === 0
      ? undefined
      : openable.find((square) => square.row.slug === slug);

  const preferred = find(inputs.preferredModule);
  if (preferred !== undefined) return destinationOf(preferred, 'chosen-default');

  if (inputs.entry === 'cold') {
    const last = find(inputs.lastModule);
    if (last !== undefined) return destinationOf(last, 'last-module');
  }

  if (openable.length === 1) return destinationOf(openable[0]!, 'only-openable');

  const classDefault = find(inputs.classDefault);
  if (classDefault !== undefined) return destinationOf(classDefault, 'class-default');

  return { kind: 'in-app', href: appsPath, branch: 'apps' };
}
